import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CloseIcon } from '@platform/ui-kit';
import { STRUCTURED_FILTERS, type FilterValues, type StructuredFilterKey } from './shared';

/**
 * The filter row, to APR's measurements.
 *
 * Taken from the running app rather than a screenshot:
 *   input   padding 6px 30px 6px 10px · font 15px · 1px #ddd · radius 4px
 *   active  2px solid #1976d2
 *   widths  free text flex:1 max 400 · vendor 250 · function 230
 *           · type 210 · criticality 220
 *
 * The compact padding is what makes these read as filters rather than as a form —
 * MUI's default input is half again as tall.
 */

const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', height: 33.24 },
  '& .MuiOutlinedInput-input': { padding: '6px 10px', fontSize: 15, height: 'auto' },
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.25px', borderColor: '#dddddd' },
} as const;

const ACTIVE_SX = {
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: '#1976d2' },
} as const;

export interface FilterBarProps {
  searchPlaceholder: string;
  text: string;
  onTextChange: (value: string) => void;
  filters: FilterValues;
  onFiltersChange: (value: FilterValues) => void;
  /** e.g. "12 of 576 shown" — rendered at the end of the row. */
  summary?: string;
  /**
   * Extra controls placed immediately after the structured filters, on the SAME row.
   *
   * Applications uses this for the two scope toggles. It is a slot rather than a fixed
   * prop because Current Run Rate shares this bar and has no use for them.
   */
  extra?: React.ReactNode;
}

/**
 * Fixed widths, measured off APR: the free-text box is 400px and the four structured
 * filters are 207.5px each. They do not flex — at 1852px all five sit on one row with
 * room to spare, and below that they wrap as whole controls.
 */
const WIDTH: Record<StructuredFilterKey, number> = {
  vendor: 207.5,
  businessFunction: 207.5,
  applicationType: 207.5,
  businessCriticality: 207.5,
};

export function FilterBar({
  searchPlaceholder,
  text,
  onTextChange,
  filters,
  onFiltersChange,
  summary,
  extra,
}: FilterBarProps) {
  const clear = (onClear: () => void, label: string) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton size="small" edge="end" onClick={onClear} aria-label={label}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        // With `extra` present the row must NOT wrap — the owner's requirement is that
        // the scope toggles sit beside the criticality filter and nothing spills onto a
        // second line. Every control here is already `flex: 0 1 auto`, so with wrapping
        // off they compress toward their content instead of dropping down. Without
        // `extra` the original wrapping behaviour is untouched.
        flexWrap: extra ? 'nowrap' : 'wrap',
        alignItems: 'center',
        gap: '12px',
        py: '8px',
        mb: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <TextField
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={searchPlaceholder}
        InputProps={text ? clear(() => onTextChange(''), 'Clear filter') : {}}
        sx={{
          width: 400,
          // `minWidth: 0` lets the widest control be the one that shrinks when the row is
          // told not to wrap; without it a flex item refuses to go below its content width
          // and pushes the toggles off the end.
          ...(extra ? { flex: '1 1 160px', minWidth: 0 } : { flex: '0 1 auto' }),
          ...FIELD_SX,
          ...(text ? ACTIVE_SX : {}),
        }}
      />

      {STRUCTURED_FILTERS.map(({ key, label }) => {
        const field = key as StructuredFilterKey;
        const value = filters[field];
        return (
          <TextField
            key={key}
            placeholder={`Filter by ${label}...`}
            value={value}
            onChange={(e) => onFiltersChange({ ...filters, [key]: e.target.value } as FilterValues)}
            InputProps={
              value
                ? clear(
                    () => onFiltersChange({ ...filters, [key]: '' } as FilterValues),
                    `Clear ${label} filter`,
                  )
                : {}
            }
            sx={{ width: WIDTH[field], flex: '0 1 auto', ...FIELD_SX, ...(value ? ACTIVE_SX : {}) }}
          />
        );
      })}

      {extra}

      {summary ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{summary}</Typography>
      ) : null}
    </Box>
  );
}
